/*
  Aetrex video lightbox (assets/video-lightbox.js)

  One shared, accessible player for every "Video link" button on the site.
  Loaded from layout/theme.liquid; its stylesheet (data-css) loads on demand.

  Triggers
    snippets/custom-button.liquid renders <a class="video-play-button" data-url="…">
    when a button has a Video link and no Link. Any element with
    [data-video-lightbox][data-url] works too. Optional: data-video-title,
    data-captions, data-captions-lang.

  Captions
    data-captions="#some-id" points at an inline <script type="text/vtt" id="some-id">
    on the page (custom-button.liquid prints one from the video_captions metaobject).
    data-captions may also be a .vtt URL (fetched; a cross-origin one needs CORS on
    the .vtt only). When a video starts muted (auto-open), captions switch on by themselves.

    Captions are drawn by this script into .avl__cues from a parsed cue list. The video
    never gets a <track>: on iPhone, Safari hands <track> files to the system player,
    which can't open same-page captions and then refuses to play the video at all
    (found Oct 2026: Our Story played without captions, failed with them). So the video
    element is set up exactly as it is for a video without captions. In the iPhone
    full-screen system player only, the cues are shown through a script-made text track,
    which has no file for the system player to fetch.

  Auto-open
    ?video=<word> or #<word> opens the trigger whose data-video-auto-open-word
    matches, set on the trigger itself or on a wrapper (image-with-text does
    that). A page load is not a click, so auto-open starts muted and shows an
    Unmute button.

  Replaces the old popup (the #videoModal markup the sections print plus its
  handler in assets/dynamic.js). That markup is still in the sections but is
  never shown.

  API     window.AetrexVideoLightbox.open(url, { title, captions }) / .close()
  Events  on document: aetrex:video-open, -play, -pause, -ended, -error, -close
          (detail: { src, title, currentTime, duration })
*/
(function () {
  'use strict';

  if (window.AetrexVideoLightbox) return;

  var doc = document;
  var root = doc.documentElement;
  var me = doc.currentScript;
  var CSS_URL = '';
  if (me) {
    CSS_URL = me.getAttribute('data-css') || '';
    if (!CSS_URL && me.src && /video-lightbox(?:[.]min)?[.]js/.test(me.src)) {
      CSS_URL = me.src.replace(/video-lightbox(?:[.]min)?[.]js/, 'video-lightbox.css');
    }
  }

  var TRIGGER = '.video-play-button[data-url], [data-video-lightbox][data-url]';
  var HIDE_AFTER = 2600;
  var DEFAULT_RATIO = '1.7778';
  var HELP_ID = 'avl-help';

  function media(q) {
    return window.matchMedia ? window.matchMedia(q) : { matches: false };
  }
  var mqReduce = media('(prefers-reduced-motion: reduce)');
  var mqFine = media('(hover: hover) and (pointer: fine)');

  // iOS ignores video.volume (hardware buttons only), so only offer a slider where it works.
  var volumeSettable = (function () {
    try {
      var v = doc.createElement('video');
      v.volume = 0.5;
      return v.volume === 0.5;
    } catch (e) {
      return false;
    }
  })();

  /* ---------- Icons ---------- */

  function line(d, width) {
    return '<path fill="none" stroke="currentColor" stroke-width="' + (width || 2) + '" stroke-linecap="round" stroke-linejoin="round" d="' + d + '"/>';
  }
  function icon(name, body) {
    return '<svg class="avl__i avl__i--' + name + '" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">' + body + '</svg>';
  }
  var SPEAKER = '<path d="M3.5 9.6v4.8c0 .6.4 1 1 1h2.8l4.1 3.5c.5.4 1.2.1 1.2-.6V5.7c0-.7-.7-1-1.2-.6L7.3 8.6H4.5c-.6 0-1 .4-1 1Z"/>';
  var ICON = {
    play: icon('play', '<path d="M8 5.6v12.8c0 .8.9 1.3 1.6.9l10-6.4c.6-.4.6-1.3 0-1.7l-10-6.4C8.9 4.3 8 4.8 8 5.6Z"/>'),
    pause: icon('pause', '<rect x="6" y="4.5" width="4" height="15" rx="1.2"/><rect x="14" y="4.5" width="4" height="15" rx="1.2"/>'),
    replay: icon('replay', '<path d="M12 5V2.2c0-.4-.5-.7-.8-.4L7.4 5.6c-.2.2-.2.5 0 .7l3.8 3.8c.3.3.8.1.8-.4V7a6 6 0 1 1-6 6.1c0-.6-.4-1.1-1-1.1s-1 .5-1 1.1A8 8 0 1 0 12 5Z"/>'),
    volHigh: icon('vol-high', SPEAKER + line('M15.6 9a4.2 4.2 0 0 1 0 6M18.4 6.3a8 8 0 0 1 0 11.4', 1.8)),
    volLow: icon('vol-low', SPEAKER + line('M15.6 9a4.2 4.2 0 0 1 0 6', 1.8)),
    muted: icon('muted', SPEAKER + line('M16 9.5l5 5M21 9.5l-5 5', 1.8)),
    fsEnter: icon('fs-enter', line('M4 9V5.5C4 4.7 4.7 4 5.5 4H9M15 4h3.5c.8 0 1.5.7 1.5 1.5V9M20 15v3.5c0 .8-.7 1.5-1.5 1.5H15M9 20H5.5C4.7 20 4 19.3 4 18.5V15')),
    fsExit: icon('fs-exit', line('M9 4v3.5C9 8.3 8.3 9 7.5 9H4M20 9h-3.5C15 9 15 8.3 15 7.5V4M15 20v-3.5c0-.8.7-1.5 1.5-1.5H20M4 15h3.5c.8 0 1.5.7 1.5 1.5V20')),
    close: icon('close', line('M6 6l12 12M18 6L6 18', 2.2)),
    cc: icon('cc', line('M5.5 5h13A2.5 2.5 0 0 1 21 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 16.5v-9A2.5 2.5 0 0 1 5.5 5Z', 1.8) + line('M10.6 10.2a2.1 2.1 0 1 0 0 3.6M16.6 10.2a2.1 2.1 0 1 0 0 3.6', 1.8))
  };

  var TEMPLATE =
    '<div class="avl__scrim" data-avl-dismiss></div>' +
    '<button class="avl__close" type="button" aria-label="Close video" data-avl-dismiss>' + ICON.close + '</button>' +
    '<div class="avl__stage">' +
      '<video class="avl__video" playsinline webkit-playsinline preload="none" disablepictureinpicture tabindex="-1"></video>' +
      '<div class="avl__cues" aria-hidden="true" hidden></div>' +
      '<div class="avl__spin" aria-hidden="true"></div>' +
      '<div class="avl__center"><button class="avl__big" type="button" data-avl-toggle aria-label="Play">' + ICON.play + ICON.pause + ICON.replay + '</button></div>' +
      '<button class="avl__unmute" type="button" data-avl-unmute hidden>' + ICON.muted + '<span>Unmute</span></button>' +
      '<div class="avl__error" hidden>' +
        '<p class="avl__error-msg" role="alert"></p>' +
        '<div class="avl__error-actions">' +
          '<button class="avl__pill" type="button" data-avl-retry>Try again</button>' +
          '<a class="avl__pill avl__pill--ghost" data-avl-direct target="_blank" rel="noopener">Open video file</a>' +
        '</div>' +
      '</div>' +
      '<div class="avl__bar" role="group" aria-label="Video controls">' +
        '<button class="avl__btn" type="button" data-avl-toggle data-avl-main aria-label="Play" autofocus>' + ICON.play + ICON.pause + ICON.replay + '</button>' +
        '<span class="avl__time avl__time--elapsed" aria-hidden="true">0:00</span>' +
        '<div class="avl__slider avl__slider--seek">' +
          '<input class="avl__input" data-avl-seek type="range" min="0" max="0" step="0.1" value="0" aria-label="Seek">' +
          '<div class="avl__track" aria-hidden="true"><div class="avl__buf"></div><div class="avl__fill"></div><div class="avl__thumb"></div></div>' +
        '</div>' +
        '<span class="avl__time avl__time--remaining" aria-hidden="true">−0:00</span>' +
        '<div class="avl__vol">' +
          '<button class="avl__btn" type="button" data-avl-mute aria-label="Mute">' + ICON.volHigh + ICON.volLow + ICON.muted + '</button>' +
          '<div class="avl__slider avl__slider--vol">' +
            '<input class="avl__input" data-avl-volume type="range" min="0" max="1" step="0.05" value="1" aria-label="Volume">' +
            '<div class="avl__track" aria-hidden="true"><div class="avl__fill"></div><div class="avl__thumb"></div></div>' +
          '</div>' +
        '</div>' +
        '<button class="avl__btn" type="button" data-avl-cc aria-label="Captions" aria-pressed="false" hidden>' + ICON.cc + '</button>' +
        '<button class="avl__btn" type="button" data-avl-fs aria-label="Full screen">' + ICON.fsEnter + ICON.fsExit + '</button>' +
      '</div>' +
      '<p class="avl__sr" data-avl-live aria-live="polite" aria-atomic="true"></p>' +
    '</div>' +
    '<p class="avl__sr" id="' + HELP_ID + '">Space or K plays and pauses. M mutes. F toggles full screen. Left and right arrows skip 5 seconds. Escape closes the video.</p>';

  // Keeps the player dark and empty until its stylesheet arrives (first open only).
  var CRITICAL =
    '.avl:not([data-avl-ready]){position:fixed;inset:0;width:100%;height:100%;max-width:none;max-height:none;margin:0;padding:0;border:0;background:rgba(23,23,23,.92)}' +
    '.avl:not([data-avl-ready])>*{visibility:hidden!important}';

  /* ---------- State ---------- */

  var dlg, stage, video, bar, cues, live, errBox, errMsg, direct;
  var btnMain, btnBig, btnMute, btnFs, btnCc, btnUnmute;
  var seekInput, seekSlider, volInput, volSlider, volWrap, elElapsed, elRemaining;

  var current = null;
  var loadedSrc = '';
  var closing = false;
  var pending = false;
  var scrubbing = false;
  var overBar = false;
  var autoMuted = false;
  var captionsOn = false;
  var lastPointer = 'mouse';
  var lastDuration = -1;
  var lastSecond = -1;
  var lastValueSync = 0;
  var textElapsed = '';
  var textRemaining = '';
  var closeTimer = 0;
  var hideTimer = 0;
  var spinTimer = 0;
  var bufferTimer = 0;
  var raf = 0;
  var cssPromise = null;
  var cssReady = false;
  var spaceTrigger = null;

  /* ---------- Small helpers ---------- */

  function noop() {}
  function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }
  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function fmt(s) {
    s = Math.max(0, Math.floor(s || 0));
    var h = Math.floor(s / 3600);
    var m = Math.floor((s % 3600) / 60);
    var sec = s % 60;
    return h ? h + ':' + pad(m) + ':' + pad(sec) : m + ':' + pad(sec);
  }

  function spoken(s) {
    s = Math.max(0, Math.round(s || 0));
    var h = Math.floor(s / 3600);
    var m = Math.floor((s % 3600) / 60);
    var sec = s % 60;
    var out = [];
    if (h) out.push(h + (h === 1 ? ' hour' : ' hours'));
    if (m) out.push(m + (m === 1 ? ' minute' : ' minutes'));
    if (sec || !out.length) out.push(sec + (sec === 1 ? ' second' : ' seconds'));
    return out.join(' ');
  }

  function flag(name, on) {
    if (!dlg) return;
    if (on) dlg.setAttribute('data-' + name, '');
    else dlg.removeAttribute('data-' + name);
  }

  function setText(el, value) {
    if (el.textContent !== value) el.textContent = value;
  }

  function isPlaying() { return !!video && !video.paused && !video.ended; }

  function duration() {
    var d = video ? video.duration : 0;
    return isFinite(d) && d > 0 ? d : 0;
  }

  function hasActivation() {
    var ua = navigator.userActivation;
    return !!(ua && ua.isActive);
  }

  function focusVisibleWithin(el) {
    var a = doc.activeElement;
    if (!a || !el || !el.contains(a)) return false;
    try { return a.matches(':focus-visible'); } catch (e) { return true; }
  }

  function focus(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) {} }
  }

  function safeUrl(value) {
    if (!value) return '';
    try {
      var u = new URL(String(value).trim(), window.location.href);
      return /^https?:$/.test(u.protocol) ? u.href : '';
    } catch (e) {
      return '';
    }
  }

  // Captions: a WebVTT file -> [{ s, e, t }] (seconds, plain text). Tags are dropped.
  function vttTime(v) {
    var m = /^(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{1,3})/.exec(String(v || '').trim());
    if (!m) return NaN;
    return (+(m[1] || 0)) * 3600 + (+m[2]) * 60 + (+m[3]) + (+(m[4] + '00').slice(0, 3)) / 1000;
  }
  function parseVtt(text) {
    var out = [];
    var blocks = String(text || '').replace(/<\\\//g, '</').replace(/\r\n?/g, '\n').split(/\n{2,}/);
    for (var i = 0; i < blocks.length; i++) {
      var lines = blocks[i].split('\n');
      for (var j = 0; j < lines.length; j++) {
        if (lines[j].indexOf('-->') < 0) continue;
        var parts = lines[j].split('-->');
        var s0 = vttTime(parts[0]), e0 = vttTime(parts[1]);
        var t = lines.slice(j + 1).join('\n').replace(/<[^>]*>/g, '')
          .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
        if (isFinite(s0) && isFinite(e0) && e0 > s0 && t) out.push({ s: s0, e: e0, t: t });
        break;
      }
    }
    out.sort(function (x, y) { return x.s - y.s; });
    return out;
  }

  function emit(type) {
    try {
      doc.dispatchEvent(new CustomEvent('aetrex:video-' + type, {
        detail: {
          src: current ? current.src : '',
          title: current ? current.title : '',
          currentTime: video ? video.currentTime || 0 : 0,
          duration: duration()
        }
      }));
    } catch (e) {}
  }

  function announce(msg) {
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { if (live) live.textContent = msg; }, 60);
  }

  /* ---------- Stylesheet ---------- */

  function ensureCss() {
    if (cssPromise) return cssPromise;
    cssPromise = new Promise(function (resolve) {
      if (!CSS_URL) { resolve(); return; }
      var link = doc.createElement('link');
      var timer = setTimeout(done, 5000);
      function done() {
        clearTimeout(timer);
        link.onload = link.onerror = null;
        resolve();
      }
      link.rel = 'stylesheet';
      link.href = CSS_URL;
      link.setAttribute('data-avl-css', '');
      link.onload = done;
      link.onerror = done;
      (doc.head || root).appendChild(link);
    }).then(function () {
      cssReady = true;
      if (dlg) dlg.setAttribute('data-avl-ready', '');
    });
    return cssPromise;
  }

  /* ---------- Build ---------- */

  function build() {
    if (dlg) return;

    var style = doc.createElement('style');
    style.textContent = CRITICAL;
    (doc.head || root).appendChild(style);

    dlg = doc.createElement('dialog');
    dlg.className = 'avl';
    dlg.setAttribute('aria-label', 'Video player');
    dlg.setAttribute('aria-describedby', HELP_ID);
    if (typeof dlg.showModal !== 'function') {
      dlg.setAttribute('role', 'dialog');
      dlg.setAttribute('aria-modal', 'true');
    }
    dlg.innerHTML = TEMPLATE;
    if (cssReady) dlg.setAttribute('data-avl-ready', '');
    doc.body.appendChild(dlg);

    function q(sel) { return dlg.querySelector(sel); }
    stage = q('.avl__stage');
    video = q('.avl__video');
    bar = q('.avl__bar');
    cues = q('.avl__cues');
    live = q('[data-avl-live]');
    errBox = q('.avl__error');
    errMsg = q('.avl__error-msg');
    direct = q('[data-avl-direct]');
    btnMain = q('[data-avl-main]');
    btnBig = q('.avl__big');
    btnMute = q('[data-avl-mute]');
    btnFs = q('[data-avl-fs]');
    btnCc = q('[data-avl-cc]');
    btnUnmute = q('[data-avl-unmute]');
    seekInput = q('[data-avl-seek]');
    seekSlider = q('.avl__slider--seek');
    volInput = q('[data-avl-volume]');
    volSlider = q('.avl__slider--vol');
    volWrap = q('.avl__vol');
    elElapsed = q('.avl__time--elapsed');
    elRemaining = q('.avl__time--remaining');

    dlg.style.setProperty('--avl-ratio', DEFAULT_RATIO);
    syncVolumeSlider();
    syncFsAvailability();
    bindPlayer();
  }

  function syncVolumeSlider() {
    if (volWrap) volWrap.classList.toggle('is-fixed', !(volumeSettable && mqFine.matches));
  }

  function syncFsAvailability() {
    if (!btnFs) return;
    var api = !!(stage.requestFullscreen || stage.webkitRequestFullscreen) &&
      !!(doc.fullscreenEnabled || doc.webkitFullscreenEnabled);
    btnFs.hidden = !api && typeof video.webkitEnterFullscreen !== 'function';
  }

  /* ---------- Player events ---------- */

  function bindPlayer() {
    dlg.addEventListener('cancel', function (e) {
      e.preventDefault();
      close();
    });
    dlg.addEventListener('close', teardown);

    dlg.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t === dlg || t.closest('[data-avl-dismiss]')) close();
      else if (t.closest('[data-avl-toggle]')) togglePlay(false);
      else if (t.closest('[data-avl-mute]')) toggleMute(false);
      else if (t.closest('[data-avl-unmute]')) unmute(true);
      else if (t.closest('[data-avl-fs]')) toggleFs();
      else if (t.closest('[data-avl-cc]')) toggleCaptions(false);
      else if (t.closest('[data-avl-retry]')) retry();
    });

    dlg.addEventListener('keydown', onKey);
    dlg.addEventListener('focusin', showUi);

    stage.addEventListener('pointerdown', function (e) {
      lastPointer = e.pointerType || 'mouse';
      showUi();
    }, true);
    stage.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'mouse') showUi();
    });
    stage.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse' || !isPlaying()) return;
      clearTimeout(hideTimer);
      hideTimer = setTimeout(hideUi, 700);
    });
    bar.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      overBar = true;
      showUi();
    });
    bar.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      overBar = false;
      scheduleHide();
    });

    // Mouse: click the picture to play/pause, double-click for full screen.
    // Touch: tap the picture to show or hide the controls.
    video.addEventListener('click', function () {
      if (lastPointer === 'mouse') {
        togglePlay(false);
      } else if (dlg.hasAttribute('data-idle') || !isPlaying()) {
        showUi();
      } else {
        hideUi();
      }
    });
    video.addEventListener('dblclick', function () {
      if (lastPointer === 'mouse') toggleFs();
    });

    video.addEventListener('loadedmetadata', function () {
      var w = video.videoWidth;
      var h = video.videoHeight;
      if (w && h) {
        var r = w / h;
        if (r > 0.25 && r < 4) dlg.style.setProperty('--avl-ratio', r.toFixed(4));
      }
      syncTime(true);
    });
    video.addEventListener('durationchange', function () { syncTime(true); });
    video.addEventListener('play', function () {
      flag('ended', false);
      syncState();
      emit('play');
    });
    video.addEventListener('playing', function () {
      setLoading(false);
      syncState();
      scheduleHide();
    });
    video.addEventListener('pause', function () {
      syncState();
      syncTime(true);
      if (!current || closing) return;
      showUi();
      if (!video.ended) emit('pause');
    });
    video.addEventListener('ended', function () {
      flag('ended', true);
      syncState();
      syncTime(true);
      showUi();
      emit('ended');
    });
    video.addEventListener('waiting', function () {
      if (!scrubbing) setLoading(true);
    });
    video.addEventListener('canplay', function () {
      if (!pending) setLoading(false);
    });
    video.addEventListener('seeked', function () {
      if (!pending && (video.paused || video.readyState >= 3)) setLoading(false);
      syncTime(true);
      renderCues();
    });
    video.addEventListener('progress', syncBuffered);
    video.addEventListener('timeupdate', function () {
      if (!raf) syncTime(false);
      renderCues();
    });
    video.addEventListener('volumechange', syncVolume);
    video.addEventListener('error', onError);
    video.addEventListener('webkitbeginfullscreen', function () { syncFs(); syncFsTrack(); });
    video.addEventListener('webkitendfullscreen', function () { syncFs(); syncFsTrack(); });

    // Seek slider. Dragging previews with fastSeek where the browser has it,
    // then lands on the exact frame on release.
    seekInput.addEventListener('pointerdown', function () {
      scrubbing = true;
      seekSlider.classList.add('is-active');
      showUi();
    });
    seekInput.addEventListener('input', function () {
      var t = +seekInput.value;
      if (scrubbing && typeof video.fastSeek === 'function') {
        try { video.fastSeek(t); } catch (e) { video.currentTime = t; }
      } else {
        try { video.currentTime = t; } catch (e) {}
      }
      seekInput.setAttribute('aria-valuetext', spoken(t) + ' of ' + spoken(duration()));
      syncTime(false);
    });
    seekInput.addEventListener('change', endScrub);
    doc.addEventListener('pointerup', endScrub);
    doc.addEventListener('pointercancel', endScrub);

    volInput.addEventListener('input', function () {
      var v = +volInput.value;
      if (volumeSettable) video.volume = v;
      video.muted = v === 0;
      if (v > 0) autoMuted = false;
    });

    doc.addEventListener('fullscreenchange', syncFs);
    doc.addEventListener('webkitfullscreenchange', syncFs);

    if (mqFine.addEventListener) mqFine.addEventListener('change', syncVolumeSlider);
  }

  function endScrub() {
    if (!scrubbing) return;
    scrubbing = false;
    seekSlider.classList.remove('is-active');
    try { video.currentTime = +seekInput.value; } catch (e) {}
    syncTime(true);
    scheduleHide();
  }

  /* ---------- Rendering state ---------- */

  function syncState() {
    if (!dlg) return;
    var playing = isPlaying();
    flag('playing', playing);
    var label = video.ended ? 'Replay' : playing ? 'Pause' : 'Play';
    btnMain.setAttribute('aria-label', label);
    btnBig.setAttribute('aria-label', label);
    btnUnmute.hidden = !(autoMuted && video.muted);
    if (playing) startLoop();
  }

  function syncVolume() {
    if (!dlg) return;
    var level = video.muted ? 0 : video.volume;
    dlg.setAttribute('data-vol', level === 0 ? 'muted' : level < 0.5 ? 'low' : 'high');
    btnMute.setAttribute('aria-label', level === 0 ? 'Unmute' : 'Mute');
    volInput.value = String(level);
    volInput.setAttribute('aria-valuetext', Math.round(level * 100) + '%');
    volSlider.style.setProperty('--p', level * 100 + '%');
    if (level > 0) autoMuted = false;
    var hadFocus = doc.activeElement === btnUnmute;
    btnUnmute.hidden = !(autoMuted && video.muted);
    if (hadFocus && btnUnmute.hidden) focus(btnMute);
  }

  function syncTime(forceValue) {
    if (!dlg || !video) return;
    var d = duration();
    var t = scrubbing ? +seekInput.value : video.currentTime || 0;
    if (d !== lastDuration) {
      lastDuration = d;
      seekInput.max = String(d || 0);
    }
    seekSlider.style.setProperty('--p', (d ? clamp(t / d, 0, 1) * 100 : 0) + '%');

    var e = fmt(t);
    var r = '−' + fmt(d - t);
    if (e !== textElapsed) { textElapsed = e; setText(elElapsed, e); }
    if (r !== textRemaining) { textRemaining = r; setText(elRemaining, r); }

    if (scrubbing) return;
    // Keep the slider's value in step with playback, but if a screen reader user
    // is sitting on the slider, only every 5 s so it isn't read out every second.
    var sec = Math.floor(t);
    var now = Date.now();
    var onSlider = doc.activeElement === seekInput;
    if (forceValue || (sec !== lastSecond && (!onSlider || !isPlaying() || now - lastValueSync > 5000))) {
      lastSecond = sec;
      lastValueSync = now;
      seekInput.value = String(t);
      seekInput.setAttribute('aria-valuetext', spoken(t) + ' of ' + spoken(d));
    }
  }

  function syncBuffered() {
    if (!dlg || !video) return;
    var d = duration();
    if (!d) return;
    var b = video.buffered;
    var t = video.currentTime || 0;
    var end = 0;
    for (var i = 0; i < b.length; i++) {
      if (b.start(i) <= t + 0.5 && b.end(i) >= t) { end = b.end(i); break; }
    }
    seekSlider.style.setProperty('--b', clamp(end / d, 0, 1) * 100 + '%');
  }

  function startLoop() {
    if (!raf) raf = requestAnimationFrame(loop);
  }
  function loop(ts) {
    raf = 0;
    syncTime(false);
    renderCues();
    if (!bufferTimer || ts - bufferTimer > 500) { bufferTimer = ts; syncBuffered(); }
    if (isPlaying() && dlg.open) raf = requestAnimationFrame(loop);
  }
  function stopLoop() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  }

  function setLoading(on) {
    clearTimeout(spinTimer);
    flag('loading', on);
    if (on) spinTimer = setTimeout(function () { flag('spin', true); }, 350);
    else flag('spin', false);
  }

  /* ---------- Controls show / hide ---------- */

  function canHide() {
    return isPlaying() && !scrubbing && !overBar && !focusVisibleWithin(bar) &&
      !dlg.hasAttribute('data-error');
  }
  function scheduleHide() {
    clearTimeout(hideTimer);
    if (dlg && canHide()) hideTimer = setTimeout(hideUi, HIDE_AFTER);
  }
  function hideUi() {
    clearTimeout(hideTimer);
    if (dlg && canHide()) flag('idle', true);
  }
  function showUi() {
    if (!dlg) return;
    if (dlg.hasAttribute('data-idle')) flag('idle', false);
    scheduleHide();
  }

  /* ---------- Playback ---------- */

  function safePlay() {
    if (!video || !current) return;
    pending = true;
    var p;
    try { p = video.play(); } catch (e) { pending = false; return; }
    if (!p || typeof p.then !== 'function') { pending = false; return; }
    p.then(function () {
      pending = false;
      syncState();
    }, function (err) {
      pending = false;
      if (!current || closing) return;
      // Sound was blocked: start muted instead and offer Unmute.
      if (err && err.name === 'NotAllowedError' && !video.muted) {
        video.muted = true;
        autoMuted = true;
        syncVolume();
        captionsForMuted();
        safePlay();
        return;
      }
      if (err && err.name === 'AbortError') return;
      setLoading(false);
      syncState();
      showUi();
    });
  }

  // A video playing without sound shows its captions by default.
  function captionsForMuted() {
    if (btnCc && !btnCc.hidden && !captionsOn) toggleCaptions(false);
  }

  function begin(withSound) {
    video.muted = !withSound;
    autoMuted = !withSound;
    syncVolume();
    if (!withSound) captionsForMuted();
    setLoading(true);
    safePlay();
  }

  function togglePlay(fromKey) {
    if (!video || !current) return;
    var bigHadFocus = doc.activeElement === btnBig;
    if (video.paused || video.ended) {
      if (video.ended) {
        try { video.currentTime = 0; } catch (e) {}
      }
      safePlay();
      if (fromKey) announce('Playing');
    } else {
      video.pause();
      if (fromKey) announce('Paused');
    }
    // The big centre button hides while playing; keep focus on a visible control.
    if (bigHadFocus) focus(btnMain);
  }

  function unmute(fromPill) {
    video.muted = false;
    if (volumeSettable && video.volume === 0) video.volume = 0.6;
    autoMuted = false;
    syncVolume();
    if (fromPill) announce('Sound on');
  }

  function toggleMute(fromKey) {
    if (video.muted || video.volume === 0) unmute(false);
    else {
      video.muted = true;
      autoMuted = false;
    }
    if (fromKey) announce(video.muted ? 'Muted' : 'Sound on');
  }

  function nudgeVolume(delta) {
    var level = clamp((video.muted ? 0 : video.volume) + delta, 0, 1);
    level = Math.round(level * 20) / 20;
    if (volumeSettable) video.volume = level;
    video.muted = level === 0;
    if (level > 0) autoMuted = false;
    announce(video.muted ? 'Muted' : 'Volume ' + Math.round((volumeSettable ? level : 1) * 100) + '%');
  }

  function seekTo(t, quiet) {
    var d = duration();
    if (!d) return;
    t = clamp(t, 0, d);
    if (t < d) flag('ended', false);
    try { video.currentTime = t; } catch (e) {}
    syncTime(true);
    if (!quiet && doc.activeElement !== seekInput) announce(spoken(t));
  }

  function skip(delta) {
    seekTo((video.currentTime || 0) + delta);
  }

  /* ---------- Full screen ---------- */

  function fsElement() {
    return doc.fullscreenElement || doc.webkitFullscreenElement || null;
  }

  function nativeFs() {
    if (video && typeof video.webkitEnterFullscreen === 'function') {
      try { video.webkitEnterFullscreen(); } catch (e) {}
    }
  }

  function toggleFs() {
    if (!stage) return;
    if (fsElement()) {
      var exit = doc.exitFullscreen || doc.webkitExitFullscreen;
      if (exit) {
        try {
          var r = exit.call(doc);
          if (r && r.catch) r.catch(noop);
        } catch (e) {}
      }
      return;
    }
    var req = stage.requestFullscreen || stage.webkitRequestFullscreen;
    if (req && (doc.fullscreenEnabled || doc.webkitFullscreenEnabled)) {
      try {
        var p = req.call(stage);
        if (p && p.catch) p.catch(nativeFs);
        return;
      } catch (e) {}
    }
    // iPhone: no element full screen, so use the system player.
    nativeFs();
  }

  function syncFs() {
    if (!dlg) return;
    var on = fsElement() === stage;
    flag('fs', on);
    btnFs.setAttribute('aria-label', on ? 'Exit full screen' : 'Full screen');
    showUi();
  }

  /* ---------- Captions ---------- */

  var cueCache = {};   // data-captions value -> parsed cues
  var cueList = null;  // cues for the open video
  var cueLang = 'en';
  var cueToken = 0;    // ignores a .vtt download that lands after the video changed
  var fsTrack = null;  // script-made track, only for the iPhone full-screen player

  function setCaptions(value, lang) {
    var old = video.querySelectorAll('track');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
    video.removeAttribute('crossorigin');
    cueToken++;
    cueList = null;
    cueLang = lang || 'en';
    captionsOn = false;
    cues.hidden = true;
    cues.textContent = '';
    btnCc.hidden = true;
    btnCc.setAttribute('aria-pressed', 'false');
    syncFsTrack();
    value = String(value || '').trim();
    if (!value) return;
    if (cueCache[value]) { useCues(cueCache[value], false); return; }
    if (value.charAt(0) === '#') {
      var el = doc.getElementById(value.slice(1));
      var list = el ? parseVtt(el.textContent) : [];
      if (list.length) { cueCache[value] = list; useCues(list, false); }
      return;
    }
    var url = safeUrl(value);
    if (!url || typeof window.fetch !== 'function') return;
    var token = cueToken;
    window.fetch(url, { credentials: 'omit' }).then(function (r) {
      return r.ok ? r.text() : '';
    }).then(function (text) {
      var got = parseVtt(text);
      if (!got.length) return;
      cueCache[value] = got;
      if (token === cueToken) useCues(got, true);
    }).catch(noop);
  }

  function useCues(list, late) {
    cueList = list;
    btnCc.hidden = false;
    // A file that arrives after playback started: a muted video still gets captions on.
    if (late && current && video.muted && !captionsOn) toggleCaptions(false);
  }

  function renderCues() {
    if (!captionsOn || !cueList) return;
    var t = video.currentTime || 0;
    var text = '';
    for (var i = 0; i < cueList.length; i++) {
      var c = cueList[i];
      if (c.s > t) break;
      if (t < c.e) text += (text ? String.fromCharCode(10) : '') + c.t;
    }
    if (cues.textContent !== text) cues.textContent = text;
    cues.hidden = !text;
  }

  // iPhone full screen uses the system player, which can't see .avl__cues.
  function syncFsTrack() {
    var want = !!(video && video.webkitDisplayingFullscreen && captionsOn && cueList &&
      typeof video.addTextTrack === 'function' && typeof window.VTTCue === 'function');
    try {
      if (!want) {
        if (fsTrack) fsTrack.mode = 'disabled';
        return;
      }
      if (!fsTrack) fsTrack = video.addTextTrack('captions', 'Captions', cueLang);
      fsTrack.mode = 'hidden';
      if (fsTrack.aetrexList !== cueList) {
        while (fsTrack.cues && fsTrack.cues.length) fsTrack.removeCue(fsTrack.cues[0]);
        for (var i = 0; i < cueList.length; i++) fsTrack.addCue(new window.VTTCue(cueList[i].s, cueList[i].e, cueList[i].t));
        fsTrack.aetrexList = cueList;
      }
      fsTrack.mode = 'showing';
    } catch (e) {}
  }

  function toggleCaptions(fromKey) {
    if (btnCc.hidden) return;
    captionsOn = !captionsOn;
    btnCc.setAttribute('aria-pressed', String(captionsOn));
    if (captionsOn) renderCues();
    else {
      cues.hidden = true;
      cues.textContent = '';
    }
    syncFsTrack();
    if (fromKey) announce(captionsOn ? 'Captions on' : 'Captions off');
  }

  /* ---------- Errors ---------- */

  function onError() {
    if (!current || !video.getAttribute('src')) return;
    pending = false;
    setLoading(false);
    stopLoop();
    flag('idle', false);
    flag('error', true);
    direct.href = current.src;
    errBox.hidden = false;
    // The code tells support why (1 aborted, 2 network, 3 decode, 4 file/format refused).
    var mediaErr = video.error;
    errMsg.textContent = 'This video couldn’t load.' + (mediaErr && mediaErr.code ? ' (error ' + mediaErr.code + ')' : '');
    if (window.console && console.warn) console.warn('[video-lightbox] media error', mediaErr && mediaErr.code, mediaErr && mediaErr.message, current.src);
    syncState();
    focus(errBox.querySelector('[data-avl-retry]'));
    emit('error');
  }

  function clearError() {
    if (!dlg) return;
    flag('error', false);
    errBox.hidden = true;
    errMsg.textContent = '';
  }

  function retry() {
    if (!current) return;
    clearError();
    focus(btnMain);
    try { video.load(); } catch (e) {}
    begin(true);
  }

  /* ---------- Keyboard ---------- */

  function focusables() {
    var scope = fsElement() === stage ? stage : dlg;
    var list = scope.querySelectorAll('button, a[href], input');
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.disabled || el.closest('[hidden]') || !el.getClientRects().length) continue;
      if (window.getComputedStyle(el).visibility === 'hidden') continue;
      out.push(el);
    }
    return out;
  }

  function trapTab(e) {
    var els = focusables();
    if (!els.length) { e.preventDefault(); return; }
    var first = els[0];
    var last = els[els.length - 1];
    var a = doc.activeElement;
    var inside = els.indexOf(a) !== -1;
    if (e.shiftKey && (a === first || !inside)) {
      e.preventDefault();
      focus(last);
    } else if (!e.shiftKey && (a === last || !inside)) {
      e.preventDefault();
      focus(first);
    }
  }

  function onKey(e) {
    if (!current) return;
    if (e.key === 'Tab') {
      showUi();
      trapTab(e);
      return;
    }
    if (e.altKey || e.ctrlKey || e.metaKey) return;

    var t = e.target;
    var isButton = t.tagName === 'BUTTON' || t.tagName === 'A';
    var onSeek = t === seekInput;
    var onOtherRange = t.type === 'range' && !onSeek;
    var d = duration();
    var handled = true;

    showUi();
    switch (e.key) {
      case ' ':
      case 'Spacebar':
        if (isButton) handled = false;
        else togglePlay(true);
        break;
      case 'k': case 'K': togglePlay(true); break;
      case 'm': case 'M': toggleMute(true); break;
      case 'f': case 'F': toggleFs(); break;
      case 'c': case 'C':
        if (btnCc.hidden) handled = false;
        else toggleCaptions(true);
        break;
      case 'j': case 'J': skip(-10); break;
      case 'l': case 'L': skip(10); break;
      case 'ArrowLeft': case 'Left':
        if (onOtherRange) handled = false;
        else skip(-5);
        break;
      case 'ArrowRight': case 'Right':
        if (onOtherRange) handled = false;
        else skip(5);
        break;
      case 'ArrowUp': case 'Up':
        if (onSeek) skip(5);
        else if (onOtherRange) handled = false;
        else nudgeVolume(0.1);
        break;
      case 'ArrowDown': case 'Down':
        if (onSeek) skip(-5);
        else if (onOtherRange) handled = false;
        else nudgeVolume(-0.1);
        break;
      case 'PageUp':
        if (onSeek) skip(Math.max(10, d * 0.1));
        else handled = false;
        break;
      case 'PageDown':
        if (onSeek) skip(-Math.max(10, d * 0.1));
        else handled = false;
        break;
      case 'Home':
        if (onOtherRange) handled = false;
        else seekTo(0);
        break;
      case 'End':
        if (onOtherRange) handled = false;
        else seekTo(d);
        break;
      case 'Escape': case 'Esc':
        // A native modal <dialog> closes itself (via 'cancel'); this covers the fallback.
        if (typeof dlg.showModal === 'function') handled = false;
        else close();
        break;
      default:
        if (/^[0-9]$/.test(e.key) && d && !onOtherRange) seekTo(d * (+e.key) / 10);
        else handled = false;
    }
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  /* ---------- Open / close ---------- */

  function lockScroll() { root.classList.add('avl-lock'); }
  function unlockScroll() { root.classList.remove('avl-lock'); }

  // A fixed element covers the layout viewport. When the page is pinch-zoomed, or
  // wider than the phone screen, that is bigger than what's visible and the close
  // button ends up off-screen. Pin the player to the visible area instead.
  var fitFrame = 0;
  function fitViewport() {
    fitFrame = 0;
    if (!dlg || !dlg.open) return;
    var vv = window.visualViewport;
    var s = dlg.style;
    var off = vv && (vv.scale > 1.01 || vv.width < window.innerWidth - 1 ||
      vv.offsetLeft > 0.5 || vv.offsetTop > 0.5);
    if (off) {
      s.left = vv.offsetLeft + 'px';
      s.top = vv.offsetTop + 'px';
      s.width = vv.width + 'px';
      s.height = vv.height + 'px';
      s.right = 'auto';
      s.bottom = 'auto';
    } else if (s.width) {
      s.left = s.top = s.width = s.height = s.right = s.bottom = '';
    }
  }
  function queueFit() {
    if (!fitFrame) fitFrame = requestAnimationFrame(fitViewport);
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', queueFit);
    window.visualViewport.addEventListener('scroll', queueFit);
  }

  function setMediaSession(on) {
    try {
      if (!('mediaSession' in navigator)) return;
      if (!on) { navigator.mediaSession.metadata = null; return; }
      if (typeof window.MediaMetadata !== 'function') return;
      var site = doc.querySelector('meta[property="og:site_name"]');
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: (current && current.title) || doc.title,
        artist: site ? site.getAttribute('content') || '' : ''
      });
    } catch (e) {}
  }

  function show() {
    if (!dlg.open) {
      try {
        if (typeof dlg.showModal === 'function') dlg.showModal();
        else dlg.setAttribute('open', '');
      } catch (e) {
        dlg.setAttribute('open', '');
      }
      lockScroll();
      fitViewport();
      void dlg.offsetWidth; // start the entrance transition from the closed state
    }
    dlg.classList.add('is-open');
    flag('idle', false);
    if (doc.activeElement !== btnMain) focus(btnMain);
  }

  function open(src, opts) {
    opts = opts || {};
    var url = safeUrl(src);
    if (!url || !doc.body) return false;

    build();
    ensureCss();

    if (dlg.open && !closing && current && current.src === url) {
      showUi();
      return true;
    }
    if (closing) {
      clearTimeout(closeTimer);
      closing = false;
      dlg.classList.remove('is-closing');
    }

    current = {
      src: url,
      title: opts.title || '',
      trigger: opts.trigger || (current && current.trigger) || null
    };
    dlg.setAttribute('aria-label', current.title ? 'Video: ' + current.title : 'Video player');

    clearError();
    stopLoop();
    flag('ended', false);
    flag('playing', false);
    flag('idle', false);
    lastDuration = -1;
    lastSecond = -1;
    textElapsed = '';
    textRemaining = '';
    if (url !== loadedSrc) dlg.style.setProperty('--avl-ratio', DEFAULT_RATIO);
    seekSlider.style.setProperty('--p', '0%');
    seekSlider.style.setProperty('--b', '0%');
    seekInput.max = '0';
    seekInput.value = '0';
    seekInput.setAttribute('aria-valuetext', spoken(0) + ' of ' + spoken(0));
    setText(elElapsed, '0:00');
    setText(elRemaining, '−0:00');

    setCaptions(opts.captions, opts.captionsLang);
    video.preload = 'auto';
    video.src = url;
    loadedSrc = url;

    // Order matters: play() must run inside the click that opened us, or
    // Safari treats it as autoplay.
    show();
    begin(!!opts.gesture);
    setMediaSession(true);
    emit('open');
    return true;
  }

  function close() {
    if (!dlg || !dlg.open || closing) return;
    closing = true;
    if (fsElement()) {
      var exit = doc.exitFullscreen || doc.webkitExitFullscreen;
      try {
        var r = exit && exit.call(doc);
        if (r && r.catch) r.catch(noop);
      } catch (e) {}
    }
    try { video.pause(); } catch (e) {}
    dlg.classList.remove('is-open');
    dlg.classList.add('is-closing');
    clearTimeout(closeTimer);
    closeTimer = setTimeout(finishClose, mqReduce.matches ? 180 : 280);
  }

  function finishClose() {
    if (!dlg.open) return teardown();
    if (typeof dlg.close === 'function' && typeof dlg.showModal === 'function') {
      dlg.close(); // fires 'close' -> teardown
    } else {
      dlg.removeAttribute('open');
      teardown();
    }
  }

  function teardown() {
    var trigger = current && current.trigger;
    if (current) emit('close');
    clearTimeout(closeTimer);
    clearTimeout(hideTimer);
    clearTimeout(spinTimer);
    stopLoop();
    closing = false;
    pending = false;
    scrubbing = false;
    overBar = false;
    autoMuted = false;
    current = null;

    dlg.classList.remove('is-open', 'is-closing');
    try { video.pause(); } catch (e) {}
    // Release the file. (Setting src = "" would make the browser request this page as a video.)
    video.removeAttribute('src');
    setCaptions('');
    video.preload = 'none';
    try { video.load(); } catch (e) {}

    clearError();
    flag('playing', false);
    flag('ended', false);
    flag('idle', false);
    setLoading(false);
    btnUnmute.hidden = true;
    unlockScroll();
    setMediaSession(false);

    if (trigger && trigger.isConnected) focus(trigger);
  }

  /* ---------- Triggers ---------- */

  function triggerFrom(node) {
    return node && node.closest ? node.closest(TRIGGER) : null;
  }

  function openTrigger(t, gesture) {
    return open(t.getAttribute('data-url'), {
      trigger: t,
      title: t.getAttribute('data-video-title') || '',
      captions: t.getAttribute('data-captions') || '',
      captionsLang: t.getAttribute('data-captions-lang') || '',
      gesture: gesture
    });
  }

  doc.addEventListener('click', function (e) {
    var t = triggerFrom(e.target);
    if (!t || (dlg && dlg.contains(t))) return;
    e.preventDefault();
    // Scripted clicks (auto-open helpers) are not a real tap: start muted.
    openTrigger(t, e.isTrusted || hasActivation());
  });

  // The trigger is an <a> with role="button": make Enter and Space open it.
  doc.addEventListener('keydown', function (e) {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    var t = triggerFrom(e.target);
    if (!t || t !== e.target) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      openTrigger(t, true);
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      spaceTrigger = t;
    }
  });
  doc.addEventListener('keyup', function (e) {
    if ((e.key === ' ' || e.key === 'Spacebar') && spaceTrigger) {
      var t = spaceTrigger;
      spaceTrigger = null;
      if (e.target === t) openTrigger(t, true);
    }
  });

  // Get the player ready the moment someone points at, focuses or touches a trigger.
  function warm(e) {
    if (!triggerFrom(e.target)) return;
    ensureCss();
    build();
    doc.removeEventListener('pointerover', warm);
    doc.removeEventListener('focusin', warm);
    doc.removeEventListener('touchstart', warm);
  }
  doc.addEventListener('pointerover', warm, { passive: true });
  doc.addEventListener('focusin', warm);
  doc.addEventListener('touchstart', warm, { passive: true });

  // A trigger whose label is hidden with CSS (e.g. a "watch" link kept only for
  // ?video=watch) would be an invisible tab stop. Take it out of the tab order
  // and the accessibility tree while nothing of it shows; put it back if it does.
  function isGhost(t) {
    if (!t.getClientRects().length) return false; // display:none is skipped by the browser already
    if ((t.innerText || '').trim()) return false;
    var media = t.querySelectorAll('svg, img');
    for (var i = 0; i < media.length; i++) {
      var r = media[i].getBoundingClientRect();
      if (r.width > 1 && r.height > 1) return false;
    }
    return true;
  }

  function syncGhosts() {
    var list = doc.querySelectorAll(TRIGGER);
    for (var i = 0; i < list.length; i++) {
      var t = list[i];
      var ghost = isGhost(t);
      if (ghost && !t.hasAttribute('data-avl-ghost')) {
        t.setAttribute('data-avl-ghost', t.getAttribute('tabindex') || '');
        t.setAttribute('tabindex', '-1');
        t.setAttribute('aria-hidden', 'true');
      } else if (!ghost && t.hasAttribute('data-avl-ghost')) {
        var was = t.getAttribute('data-avl-ghost');
        if (was) t.setAttribute('tabindex', was);
        else t.removeAttribute('tabindex');
        t.removeAttribute('aria-hidden');
        t.removeAttribute('data-avl-ghost');
      }
    }
  }

  var ghostTimer = 0;
  window.addEventListener('resize', function () {
    clearTimeout(ghostTimer);
    ghostTimer = setTimeout(syncGhosts, 250);
  });
  window.addEventListener('load', syncGhosts);

  // Leaving the page (or going into the back/forward cache) with the player open.
  window.addEventListener('pagehide', function () {
    if (dlg && dlg.open) {
      if (typeof dlg.close === 'function' && typeof dlg.showModal === 'function') dlg.close();
      else { dlg.removeAttribute('open'); teardown(); }
    }
  });

  /* ---------- Auto-open from the URL ---------- */

  function handle(value) {
    return String(value || '').toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function urlWord() {
    var word = '';
    try {
      word = new URLSearchParams(window.location.search).get('video') ||
        decodeURIComponent(window.location.hash.slice(1));
    } catch (e) {
      word = window.location.hash.slice(1);
    }
    return handle(word);
  }

  function autoOpen(gesture) {
    var word = urlWord();
    if (!word) return;
    var marks = doc.querySelectorAll('[data-video-auto-open-word]');
    for (var i = 0; i < marks.length; i++) {
      if (handle(marks[i].getAttribute('data-video-auto-open-word')) !== word) continue;
      var t = marks[i].matches(TRIGGER) ? marks[i] : marks[i].querySelector(TRIGGER);
      if (t) {
        openTrigger(t, gesture);
        return;
      }
    }
  }

  function onReady(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  onReady(function () {
    setTimeout(function () {
      if (doc.querySelector(TRIGGER)) {
        if (window.requestIdleCallback) window.requestIdleCallback(ensureCss, { timeout: 4000 });
        else setTimeout(ensureCss, 1500);
      }
      syncGhosts();
      autoOpen(false);
    }, 0);
  });

  window.addEventListener('hashchange', function () {
    if (!dlg || !dlg.open) autoOpen(hasActivation());
  });

  /* ---------- Public API ---------- */

  window.AetrexVideoLightbox = {
    open: function (url, opts) {
      opts = opts || {};
      return open(url, {
        title: opts.title || '',
        captions: opts.captions || '',
        captionsLang: opts.captionsLang || '',
        trigger: opts.trigger || null,
        gesture: opts.muted ? false : hasActivation()
      });
    },
    close: close,
    isOpen: function () { return !!(dlg && dlg.open && !closing); }
  };
})();
